import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExoStepper, { FinalPage } from '../../../../components/exo/ExoStepper';
import StepDelivery from '../../../../components/steps/StepDelivery';
import ExoDialog from '../../../../components/exo/ExoDialog';
import { API_POST, DBRequest, useConfig } from '../../../../api/api';
import StepCustomStartUp from '../../../../components/steps/StepCustomStartUp';

const QuickEditContract = ({
  open,
  onClose,
  contract,
  employeeId,
  onCreate,
  employeeInfo,
  debug = false,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [steps, setSteps] = useState([]);

  const fieldToIdentifierMap = {
    salary: '%%salary%%',
    hoursPerWeek: '%%hoursPerWeek%%',
    vacation: '%%vacationDays%%',
    daysInWeek: '%%workdays%%',
    startDate: '%%startDate%%',
  };

  useEffect(() => {
    if (!contract) return;
    // Map contract vars to form fields
    const weekDayOptions = [
      { label: t('Monday'), value: 1 },
      { label: t('Tuesday'), value: 2 },
      { label: t('Wednesday'), value: 3 },
      { label: t('Thursday'), value: 4 },
      { label: t('Friday'), value: 5 },
      { label: t('Saturday'), value: 6 },
      { label: t('Sunday'), value: 7 },
    ];

    const fields = [
      { label: t('Salary'), type: 'money', key: 'salary' },
      { label: t('Hours per Week'), type: 'number', key: 'hoursPerWeek' },
      { label: t('Vacation'), type: 'text', key: 'vacation' },
      {
        label: t('Work Days'),
        type: 'customMultiSelect',
        key: 'daysInWeek',
        options: weekDayOptions,
        noUnselect: true,
      },
      { label: t('Adjustment Effective From'), type: 'date', key: 'startDate' },
    ];

    setSteps([
      {
        label: t('Contract Info'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
          activeStep,
          setActiveStep,
        }) => (
          <StepCustomStartUp
            fields={fields}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        ),
      },
      {
        label: t('Delivery'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepDelivery
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            recipientKey="user"
          />
        ),
      },
    ]);
  }, [contract]);

  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setIsLoading,
      handleError,
    }) => {
      const updatedVars = contract.vars.map((v) => {
        const fieldKey = Object.keys(fieldToIdentifierMap).find(
          (key) => fieldToIdentifierMap[key] === v.identifier,
        );

        if (!fieldKey) return v;

        return {
          ...v,
          value: dataCollection[fieldKey],
        };
      });

      const varsObject = updatedVars.reduce((acc, v) => {
        acc[v.id] = v.value;
        return acc;
      }, {});

      const payload = {
        user: { id: employeeId },
        preset: { id: contract.contractPresetId },
        delivery: {
          ...dataCollection?.delivery,
        },
        vars: varsObject,
      };

      setIsLoading(true);

      DBRequest({
        config,
        path: 'employee-contracts',
        method: API_POST(payload),
        onResponse: (data) => {
          onCreate(data);
          onClose();
        },
        onLoading: setIsLoading,
        onError: handleError,
      });
    },
    content: (response) => <FinalPage response={response} />,
  };

  const startData = {
    salary: contract?.salary,
    hoursPerWeek: contract?.hoursPerWeek,
    vacation: contract?.vacation,
    daysInWeek: contract?.daysInWeek,
    startDate: contract?.startDate,

    user: {
      emails: employeeInfo?.email
        ? [
            {
              emailAddress: employeeInfo.email,
            },
          ]
        : [],
      firstName: employeeInfo?.firstName,
      lastName: employeeInfo?.lastName,
      salutation: employeeInfo?.salutation,
    },
  };

  return (
    <ExoDialog open={open} onClose={onClose}>
      <ExoStepper
        label={t('Adjust Contract')}
        draftType="work-contract"
        onClose={onClose}
        data={steps}
        finalStep={finalStep}
        startDataCollection={startData}
        noRecipientType
        debug={debug}
      />
    </ExoDialog>
  );
};

export default QuickEditContract;
